import {
  Directive,
  EmbeddedViewRef,
  Input,
  TemplateRef,
  ViewContainerRef,
  ɵstringify as stringify,
  inject,
} from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { FeatureFlagService } from './feature-flag.service'

function assertTemplate(
  property: string,
  templateRef: TemplateRef<any> | null
): void {
  const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView)
  if (!isTemplateRefOrNull) {
    throw new Error(
      `${property} must be a TemplateRef, but received '${stringify(
        templateRef
      )}'.`
    )
  }
}

/**
 * @publicApi
 */
export class FeatureFlagContext<T = unknown> {
  public $implicit: string

  public featureFlag: string
}

@UntilDestroy()
@Directive({
  selector: '[featureFlag]', // eslint-disable-line
  standalone: true,
})
export class FeatureFlagDirective<T = unknown> {
  public featureService = inject(FeatureFlagService)

  protected _viewContainer = inject(ViewContainerRef)

  /**
   * Assert the correct type of the expression bound to the `featureFlag` input within the template.
   *
   * The presence of this static field is a signal to the Ivy template type check compiler that
   * when the `NgIf` structural directive renders its template, the type of the expression bound
   * to `ngIf` should be narrowed in some way. For `NgIf`, the binding expression itself is used to
   * narrow its type, which allows the strictNullChecks feature of TypeScript to work with `NgIf`.
   */
  static ngTemplateGuard_ngIf: 'binding' // eslint-disable-line

  protected _context: FeatureFlagContext<T> = new FeatureFlagContext<T>()

  protected _thenTemplateRef: TemplateRef<FeatureFlagContext<T>> | null = null

  protected _elseTemplateRef: TemplateRef<FeatureFlagContext<T>> | null = null

  protected _thenViewRef: EmbeddedViewRef<FeatureFlagContext<T>> | null = null

  protected _elseViewRef: EmbeddedViewRef<FeatureFlagContext<T>> | null = null

  constructor() {
    const templateRef = inject<TemplateRef<FeatureFlagContext<T>>>(TemplateRef)

    this._thenTemplateRef = templateRef
  }

  /**
   * Asserts the correct type of the context for the template that `NgIf` will render.
   *
   * The presence of this method is a signal to the Ivy template type-check compiler that the
   * `NgIf` structural directive renders its template with a specific context type.
   */
  public static ngTemplateContextGuard<T>(
    dir: FeatureFlagDirective<T>,
    ctx: any
  ): ctx is FeatureFlagContext<Exclude<T, false | 0 | '' | null | undefined>> {
    return true
  }

  /* eslint-disable */
  /**
   * The Boolean expression to evaluate as the condition for showing a template.
   */
  @Input()
  set featureFlag(key: string) {
    this._context.$implicit = this._context.featureFlag = key
    this._updateView()
  }

  /**
   * A template to show if the condition expression evaluates to true.
   */
  @Input()
  set flagEnabled(templateRef: TemplateRef<FeatureFlagContext<T>> | null) {
    assertTemplate('flagEnabled', templateRef)
    this._thenTemplateRef = templateRef
    this._thenViewRef = null // clear previous view if any.
    this._updateView()
  }

  /**
   * A template to show if the condition expression evaluates to false.
   */
  @Input()
  set featureFlagElse(templateRef: TemplateRef<FeatureFlagContext<T>> | null) {
    this._elseTemplateRef = templateRef
    this._updateView()
  }

  /**
   * A template to show if the condition expression evaluates to false.
   */
  @Input()
  set flagDisabled(templateRef: TemplateRef<FeatureFlagContext<T>> | null) {
    assertTemplate('flagDisabled', templateRef)
    this._elseTemplateRef = templateRef
    this._elseViewRef = null // clear previous view if any.
    this._updateView()
  }

  /* eslint-enable */

  protected _updateView(): void {
    this.featureService
      .watch(this._context.featureFlag, false)
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => {
        if (enabled) {
          if (!this._thenViewRef) {
            this._viewContainer.clear()
            this._elseViewRef = null
            if (this._thenTemplateRef) {
              this._thenViewRef = this._viewContainer.createEmbeddedView(
                this._thenTemplateRef,
                this._context
              )
            }
          }
        } else {
          if (!this._elseViewRef) {
            this._viewContainer.clear()
            this._thenViewRef = null
            if (this._elseTemplateRef) {
              this._elseViewRef = this._viewContainer.createEmbeddedView(
                this._elseTemplateRef,
                this._context
              )
            }
          }
        }
      })
  }
}
